import { type ReactNode } from 'react';
import type { GameState, Round } from '@bangos/shared';
import { useT, useTranslateSpectrum } from '../i18n';
import '../styles/gameplay.css';

interface GameOverScreenProps {
  gameState: GameState;
  onPlayAgain?: () => void;
}

export function GameOverScreen({ gameState, onPlayAgain }: GameOverScreenProps): ReactNode {
  const t = useT();
  const tSpectrum = useTranslateSpectrum();
  const { phase } = gameState;
  if (phase !== 'gameOver') return null;

  const allRounds: Round[] = [...gameState.roundHistory];
  if (gameState.currentRound) {
    allRounds.push(gameState.currentRound);
  }

  return (
    <div className="gameover-container">
      <div className="gameover-title">{t('gameOver.title')}</div>
      <div className="gameover-winner">
        {gameState.blueScore > gameState.redScore
          ? t('gameOver.blueWins')
          : gameState.redScore > gameState.blueScore
            ? t('gameOver.redWins')
            : t('gameOver.draw')}
      </div>
      <div className="gameover-scores">
        <div className="gameover-score-card">
          <div className="gameover-score-team" style={{ color: '#4a90d9' }}>{t('gameOver.blueTeam')}</div>
          <div className="gameover-score-value">{gameState.blueScore}</div>
        </div>
        <div className="gameover-score-card">
          <div className="gameover-score-team" style={{ color: '#d94a4a' }}>{t('gameOver.redTeam')}</div>
          <div className="gameover-score-value">{gameState.redScore}</div>
        </div>
      </div>

      {allRounds.length > 0 && (
        <div className="gameover-recap" data-testid="round-recap">
          <div className="gameover-recap-title">{t('gameOver.roundRecap')}</div>
          {allRounds.map((round, idx) => {
            const roundNum = idx + 1;
            const cue = round.clues.length > 0 ? round.clues[0] : null;
            const guess = round.guesses.length > 0 ? round.guesses[0] : null;
            const points = round.score?.points ?? '?';
            const distance = round.score?.distance ?? '?';
            const target = round.targetPosition ?? '?';

            return (
              <div key={idx} className="gameover-recap-round" data-testid={`recap-round-${roundNum}`}>
                <div className="gameover-recap-header">
                  <span className="gameover-recap-round-num">{t('gameOver.round', { num: roundNum })}</span>
                  {cue && <span>{t('gameOver.clueValue', { text: cue.text })}</span>}
                </div>
                <div className="gameover-recap-spectrum">
                  <span style={{ color: '#4a90d9' }}>{tSpectrum(round.spectrum).left}</span>
                  <span style={{ color: '#d94a4a' }}>{tSpectrum(round.spectrum).right}</span>
                </div>
                <div className="gameover-recap-row">
                  <span className="gameover-recap-label">{t('gameOver.clue')}</span>
                  <span className="gameover-recap-value">{cue ? cue.text : '—'}</span>
                </div>
                <div className="gameover-recap-row">
                  <span className="gameover-recap-label">{t('gameOver.targetPosition')}</span>
                  <span className="gameover-recap-value">{target}</span>
                </div>
                <div className="gameover-recap-row">
                  <span className="gameover-recap-label">{t('gameOver.guessPosition')}</span>
                  <span className="gameover-recap-value">{guess ? guess.position : '—'}</span>
                </div>
                <div className="gameover-recap-row">
                  <span className="gameover-recap-label">{t('gameOver.distance')}</span>
                  <span className="gameover-recap-value">{distance}</span>
                </div>
                <div className="gameover-recap-row">
                  <span className="gameover-recap-label">{t('gameOver.points')}</span>
                  <span className="gameover-recap-value" style={{ fontWeight: 800 }}>{points}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {onPlayAgain && (
        <button className="play-again-btn" onClick={onPlayAgain}>
          {t('gameOver.playAgain')}
        </button>
      )}
    </div>
  );
}
